<template>
    <modal :open="showModal" :large="true" @close="closeModal">
        <template v-if="showModal">
            <div class="modal-header bg-primary">
                <button @click="closeModal()" type="button" class="close" aria-label="Close">
                    <span aria-hidden="true">&times;</span>
                </button>
                <div class="text-center">
                    <h4 class="modal-title">{{ $t('users.roles_from', { application: application.name }) }}</h4>
                </div>
            </div>
            <div class="modal-body">
                <div class="md:flex-row flex flex-col">
                    <aside class="md:w-220px w-full">
                        <h5 class="mt-0">{{ $t('users.roles') }}</h5>
                        <div class="list-group">
                            <a class="list-group-item px-2 py-2" :class="{ active : selectedRoleId === role.id }" href="javascript:;" @click="setSelectedRoleId(role.id)" v-for="role in roles">
                                <span class="icon icon-users icon-lg icon-fw"></span>
                                {{ role.name }}
                            </a>
                        </div>
                        <div class="mt-5">
                            <button @click="createRole" class="btn btn-outline-primary btn-block my-2">
                                {{ $t('users.add_role') }}
                            </button>
                        </div>
                    </aside>
                    <div class="md:pl-15px flex-1">
                        <template v-for="role in roles">
                            <div v-if="selectedRoleId === role.id">
                                <div class="flex items-center justify-between mb-4">
                                    <div v-if="role.organization_id">
                                        <div v-if="showNameInput === false" class="h-30px flex items-center">
                                            <h4 @click="showNameInput = true" class="border-gray-body mt-0 mb-1 border-b-2 border-dotted" v-tooltip="{ content: $t('users.edit_name_click'), placement: 'right' }">
                                                {{ role.name }}
                                            </h4>
                                        </div>
                                        <div v-else class="flex">
                                            <input class="form-control input-sm mr-3" type="text" :value="role.name" @input="newRoleName = $event.target.value">
                                            <button @click="changeRoleName(newRoleName)" class="btn btn-primary btn-sm mr-1" type="button">
                                                {{ $t('globals.save') }}
                                            </button>
                                            <button @click="showNameInput = false" class="btn btn-outline-default btn-sm" type="button">
                                                {{ $t('globals.cancel') }}
                                            </button>
                                        </div>
                                    </div>
                                    <div v-else>
                                        <h4 class="mt-0 mb-1 border-b-2 border-transparent border-dotted">
                                            {{ role.name }}
                                        </h4>
                                    </div>
                                    <button v-if="role.organization_id" @click="showDeleteConfirmation = true" class="btn btn-outline-danger btn-xs" type="button">
                                        <span class="icon icon-trash icon-fw"></span>
                                        {{ $t('users.delete_role') }}
                                    </button>
                                </div>
                                <div v-if="!role.organization_id" class="w-full mb-6">
                                    <div class="border-warning-500 p-4 bg-gray-200 border-l-4 rounded shadow" role="alert">
                                        <p class="mb-0">{{ $t('users.predefined_role_info') }}</p>
                                    </div>
                                </div>
                                <div v-if="showDeleteConfirmation" class="w-full mb-6">
                                    <div class="border-danger-500 p-4 bg-gray-200 border-l-4 rounded shadow" role="alert">
                                        <button @click="showDeleteConfirmation = false" type="button" class="close" data-dismiss="alert" aria-label="Close">
                                            <span aria-hidden="true">×</span>
                                        </button>
                                        <p class="mb-0">
                                            {{ $t('users.delete_role_info') }}
                                            <a @click="deleteRole" class="delete ml-1" href="javascript:;">
                                                {{ $t('users.yes_delete_role') }}
                                            </a>
                                        </p>
                                    </div>
                                </div>
                                <div class="gap-2.5 grid grid-cols-1 lg:grid-cols-2">
                                    <div class="card input-border-color mb-0" v-for="permissionGroup in groupedPermissions">
                                        <div class="card-body lg:px-4 px-3">
                                            <div class="flex flex-col">
                                                <h5 class="mt-0 text-base">{{ permissionGroup[0].scope }}</h5>
                                                <div class="space-y-10px flex flex-col">
                                                    <div class="flex space-x-2" v-for="permission in permissionGroup">
                                                        <label class="switch switch-primary">
                                                            <template v-if="role.organization_id">
                                                                <input @change="toggleRolePermission(role, permission, $event.target.checked)" class="switch-input" type="checkbox" :checked="role.permissions.some(selectedRolePermission => selectedRolePermission.id === permission.id)">
                                                            </template>
                                                            <template v-else>
                                                                <input class="switch-input" type="checkbox" :checked="role.permissions.some(selectedRolePermission => selectedRolePermission.id === permission.id)" disabled="disabled">
                                                            </template>
                                                            <span class="switch-track"></span>
                                                            <span class="switch-thumb"></span>
                                                        </label>
                                                        <div>
                                                            {{ permission.action }}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
            <div class="modal-footer"></div>
        </template>
    </modal>
</template>

<script>
import Modal from '../../../user/partials/modal.vue';

export default {
    components: {
        'modal': Modal,
    },

    data() {
        return {
            application: null,
            roles: [],
            selectedRoleId: null,
            showDeleteConfirmation: false,
            showNameInput: false,
            newRoleName: '',
            permissions: [],
            showModal: false,
            form: new Form({
                role_id: '',
                permission_id: '',
            }),
        };
    },

    created() {
        Bus.$on('showRoleManagementModal', ({ application }) => {
            this.openModal(application);
        });
    },

    computed: {
        groupedPermissions() {
            return _.groupBy(this.permissions, 'scope');
        },
    },

    methods: {
        openModal(application) {
            const rolesRequest = axios.get('/api/v1/application-roles', { params: { application_id: application.id } });
            const permissionsRequest = axios.get('/api/v1/application-permissions', { params: { application_id: application.id } });

            axios.all([rolesRequest, permissionsRequest])
                .then(axios.spread((...responses) => {
                    const rolesRequestResponse = responses[0];
                    const permissionsRequestResponse = responses[1];

                    this.roles = rolesRequestResponse.data;
                    this.selectedRoleId = this.roles[0] ? this.roles[0].id : null;
                    this.permissions = permissionsRequestResponse.data;
                }))
                .then(() => this.application = application)
                .then(() => this.showModal = true)
                .catch(() => {
                    window.toastr.clear();
                    window.toastr.error(this.$t('globals.unexpected_error'));
                });
        },

        closeModal() {
            Bus.$emit('updateActiveUserListPageRoles');
            this.showModal = false;
            this.application = null;
            this.selectedRoleId = null;
            this.showDeleteConfirmation = false;
            this.showNameInput = false;
            this.newRoleName = '';
            this.roles = [];
            this.resetForm();
        },

        resetForm() {
            this.form = new Form({
                role_id: '',
                permission_id: '',
            });
        },

        setSelectedRoleId(roleId) {
            this.showDeleteConfirmation = false;
            this.showNameInput = false;
            this.newRoleName = '';
            this.selectedRoleId = roleId;
        },

        toggleRolePermission(role, permission, isAttaching) {
            this.form.role_id = role.id;
            this.form.permission_id = permission.id;
            const modifiedRole = this.roles.find(role => role.id === this.form.role_id);
            if (! modifiedRole) {
                return;
            }

            Globals.post('/api/v1/toggle-role-permission', this.form)
                .then(() => {
                    if (isAttaching) {
                        const permission = this.permissions.find(permission => permission.id === this.form.permission_id);
                        modifiedRole.permissions.push(permission);
                    } else {
                        modifiedRole.permissions = modifiedRole.permissions.filter(permission => permission.id !== this.form.permission_id);
                    }
                })
                .catch(() => {
                    const errors = this.form.errors.flatten();
                    const message = errors.length > 0 ? errors[0] : this.$t('globals.unexpected_error');
                    window.toastr.clear();
                    window.toastr.error(message);
                    this.resetForm();
                });
        },

        createRole() {
            const form = new Form({
                application_id: this.application.id,
            });

            Globals.post('/api/v1/roles', form)
                .then(response => {
                    this.roles.push(response);
                    this.setSelectedRoleId(response.id);
                })
                .catch(() => {
                    const errors = form.errors.flatten();
                    const message = errors.length > 0 ? errors[0] : this.$t('globals.unexpected_error');
                    window.toastr.clear();
                    window.toastr.error(message);
                });
        },

        deleteRole() {
            const form = new Form({});

            Globals.delete(`/api/v1/roles/${this.selectedRoleId}`, form)
                .then(() => {
                    this.roles = this.roles.filter(role => role.id !== this.selectedRoleId);
                    const selectedRoleId = this.roles[0] ? this.roles[0].id : null;
                    this.setSelectedRoleId(selectedRoleId);
                })
                .catch(() => {
                    const errors = form.errors.flatten();
                    const message = errors.length > 0 ? errors[0] : this.$t('globals.unexpected_error');
                    window.toastr.clear();
                    window.toastr.error(message);
                });
        },

        changeRoleName(newName) {
            const form = new Form({
                custom_name: newName,
            });

            Globals.put(`/api/v1/roles/${this.selectedRoleId}`, form)
                .then(response => {
                    const key = this.roles.findIndex(role => role.id === this.selectedRoleId);
                    if (key) {
                        this.roles[key] = response;
                        this.setSelectedRoleId(response.id);
                    }
                })
                .catch(() => {
                    const errors = form.errors.flatten();
                    const message = errors.length > 0 ? errors[0] : this.$t('globals.unexpected_error');
                    window.toastr.clear();
                    window.toastr.error(message);
                });
        },
    },
};
</script>
